import React, { useState } from 'react';
import { AutoSuggestAddress } from '@snsw/react-component-library';
import * as PropTypes from 'prop-types';
import {
    AddressValidatorConfig,
    getDefaultAutoSuggestedAddressValidatorConfig,
    getDefaultManualAddressValidatorConfig,
    stripAddressErrors,
    validateAndAnnotateAddress
} from '../../Validators/addressValidator';
import { ImpactedAddressMode, ImpactedAddressType } from '../../store';

export const ImpactedAddress = (
    {
        apiOptions = { key: 'not-set', outFields: ['lgaName', 'lgaShortName', 'lgaPid'] },
        helpMessage = 'Start typing to search for an address or you can enter it manually. This needs to be the full ' +
        'address as stated on supporting documents such as utility bills or council rates notices',
        initialMode = 'auto',
        initialValue = {},
        label = 'Enter impacted property',
        id = 'impactedAddress',
        onChangeEvent = (value: any) => value,
        onSwitch = () => { },
        persistentAddress = 'both',
        errorConfig = undefined,
        autoValidatorConfig = getDefaultAutoSuggestedAddressValidatorConfig(),
        manualValidatorConfig = getDefaultManualAddressValidatorConfig(),
        showErrors = false,
    }: ImpactedAddressProps) => {

    const [mode, setMode] = useState(initialMode);

    const onSwitchMode = (newMode: ImpactedAddressMode) => {
        setMode(newMode);
        onSwitch(newMode);
    };

    const withValidation = (address: Partial<ImpactedAddressType>) => {
        if (showErrors) {
            const config = (mode === 'auto') ? autoValidatorConfig : (manualValidatorConfig || errorConfig);
            return validateAndAnnotateAddress(config, address);
        } else {
            return stripAddressErrors(address);
        }
    };

    const [value, setValue] = useState(withValidation(initialValue));

    const onSelect = (address, key, isComplete) => {
        const validatedAddress = withValidation(address);
        onChangeEvent(validatedAddress);
        setValue(validatedAddress);
    };

    const onChange = (address, key, isComplete) => {
        const validatedAddress = withValidation(address);
        onChangeEvent(validatedAddress);
        setValue(validatedAddress);
    };

    const onBlur = (address, key, isComplete, inputValue, highlightedValue) => {
        const validatedAddress = withValidation(address);
        onChangeEvent(validatedAddress);
        setValue(validatedAddress);
    };

    return <>
        <AutoSuggestAddress
            id={ id }
            value={
                /* withValidation depends on showErrors - the call here is necessary for immediate inline errors */
                withValidation(value)
            }
            onSelect={ onSelect }
            onChange={ onChange }
            onBlur={ onBlur }
            setValue={ setValue }
            label={ label }
            helpMessage={ helpMessage }
            onSwitch={ onSwitchMode }
            apiOptions={ apiOptions }
            initialMode={ initialMode }
            persistentAddress={ persistentAddress }
        />
    </>;
};

type ImpactedAddressProps = Partial<{
    apiOptions: { key: string, outFields: string[] },
    /** @deprecated Please use manualValidatorConfig. */
    errorConfig: AddressValidatorConfig,
    /**
     * The error validator config to use for auto-suggested addresses.
     * You will likely also want to change the validation for page submission to match.
     * @see checkImpactedAddress
     * */
    autoValidatorConfig: AddressValidatorConfig,
    /** The error validator config to use for manually-inputted addresses. */
    manualValidatorConfig: AddressValidatorConfig,
    helpMessage: string,
    id: string,
    initialMode: ImpactedAddressMode,
    initialValue: object,
    label: string,
    onChangeEvent: (value: any) => typeof value,
    onSwitch: (mode: ImpactedAddressMode) => void,
    persistentAddress: ImpactedAddressMode | 'both' | null,
    showErrors: boolean,
}>

ImpactedAddress.propTypes = {
    apiOptions: PropTypes.shape({
        key: PropTypes.string,
        outFields: PropTypes.arrayOf(PropTypes.string)
    }),
    errorConfig: PropTypes.object,
    autoValidatorConfig: PropTypes.object,
    manualValidatorConfig: PropTypes.object,
    helpMessage: PropTypes.string,
    id: PropTypes.string,
    initialMode: PropTypes.oneOf(['auto', 'manual']),
    initialValue: PropTypes.object,
    label: PropTypes.string,
    // we are not allowing the caller to use these now, (we only pass the change event back)
    //   we could later add implementation to pass on these events too
    //   onSelect: PropTypes.func,
    //   onChange: PropTypes.func,
    //   onBlur: PropTypes.func,
    onChangeEvent: PropTypes.func,
    onSwitch: PropTypes.func,
    persistentAddress: PropTypes.oneOf(['auto', 'manual', 'both', null]),
    showErrors: PropTypes.bool,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default ImpactedAddress;
